<template>
  <div class="w-full">
    <div class="d-flex justify-center w-full flex-column">
      <div
        v-if="showDropZone"
        ref="dropZone"
        class="drop-zone"
        :aria-label="$t('labels.video-upload.drop-zone-for-videos')"
        @dragover.prevent
        @dragenter.prevent
        @drop="onFileDrop"
        @click="onUploadClick"
      >
        <p class="drop-zone__prompt">
          {{ $t('labels.video-upload.drop-video') }}
        </p>
      </div>
      <v-btn
        v-else
        class="px-6 w-full text-caption font-weight-bold"
        :disabled="disabled"
        @click="onUploadClick"
      >
        <v-icon
          size="small"
          class="pr-2"
        >
          mdi-upload
        </v-icon>
        <span>{{ $t('buttons.video-upload.cta') }}</span>
      </v-btn>
      <input
        ref="FileInput"
        type="file"
        :accept="accept"
        style="display: none"
        @change="handleFileSelect"
      />
    </div>
    <v-dialog
      v-if="dialogWidth"
      v-model="dialog"
      :width="dialogWidth"
      :fullscreen="$vuetify.display.smAndDown"
    >
      <v-card class="d-flex flex-column wrapper">
        <v-card-text class="pa-4 video-preview-container">
          <div
            v-if="processing"
            class="d-flex flex-column justify-center align-center text-h5 text-center"
          >
            <v-progress-circular
              :size="150"
              :width="10"
              indeterminate
              color="primary"
              class="ma-12"
            />
            {{ $t('alerts.video-upload.in-progress', { readablePlatformName }) }}
          </div>

          <div
            v-show="videoUrl && !processing"
            class="video-preview"
          >
            <video
              ref="videoPlayer"
              :src="videoUrl"
              controls
              class="w-full"
              :alt="$t('labels.video-upload.selected-video')"
            />
          </div>

          <div
            v-if="!processing"
            class="pt-6"
          >
            <v-alert
              v-if="hasErrors"
              type="error"
            >
              <span v-if="!isVideoMimeTypeSupported"
                >{{ $t('alerts.video-upload.error-mime-type') }}<br
              /></span>
              <span v-if="isVideoFileToBig"
                >{{ $t('alerts.video-upload.error-file', { max: maxVideoFileSize / (1024 * 1024) })
                }}<br
              /></span>
              <span v-if="isVideoDurationTooLong"
                >{{ $t('alerts.video-upload.error-duration', { max: maxVideoDuration }) }}<br
              /></span>
            </v-alert>
            <v-alert
              v-else
              type="info"
              class="text-body-2"
            >
              {{ $t('alerts.video-upload.info-text') }}
            </v-alert>
          </div>

          <v-checkbox
            v-if="askForVideoRights"
            v-model="hasVideoRights"
            class="mx-4"
            color="primary"
            :label="$t('alerts.video-upload.confirm-rights')"
          />
        </v-card-text>

        <v-card-actions class="justify-space-between">
          <v-btn
            variant="text"
            @click="dialog = false"
          >
            {{ $t('labels.cancel') }}
          </v-btn>
          <v-btn
            variant="text"
            color="primary"
            :disabled="hasErrors || (askForVideoRights && !hasVideoRights)"
            @click="(saveVideo(), (dialog = false))"
          >
            {{ $t('labels.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useBranding } from '@/composables/useBranding'
import { VIDEO_MIME_TYPES } from '@/modules/adMaterials/fileTypes'

type Props = {
  name?: string
  save?: (file: File) => void
  accept?: string
  askForVideoRights?: boolean
  disabled?: boolean
  showDropZone?: boolean
  maxVideoFileSize?: number
  maxVideoDuration?: number
}

const props = withDefaults(defineProps<Props>(), {
  name: '',
  save: () => {},
  accept: 'video/mp4,video/quicktime,video/x-msvideo,video/x-ms-wmv',
  askForVideoRights: false,
  disabled: false,
  showDropZone: false,
  maxVideoFileSize: 209715200,
  maxVideoDuration: 240,
})

defineEmits<{
  input: []
}>()

const { t } = useI18n()
const { readablePlatformName } = useBranding()
const instance = getCurrentInstance()
const $tracking = instance?.appContext.config.globalProperties.$tracking

const FileInput = ref<HTMLInputElement | null>(null)
const dropZone = ref<HTMLDivElement | null>(null)
const videoPlayer = ref<HTMLVideoElement | null>(null)

const mimeType = ref('')
const videoUrl = ref('')
const videoFile = ref<File | null>(null)
const dialog = ref(false)
const processing = ref(false)
const dialogWidth = ref<number | undefined>(undefined)
const videoFileSize = ref(0)
const videoDuration = ref(0)
const hasVideoRights = ref(false)

const isVideoFileToBig = computed(() => {
  return videoFileSize.value > props.maxVideoFileSize
})

const isVideoDurationTooLong = computed(() => {
  return videoDuration.value > props.maxVideoDuration
})

const isVideoMimeTypeSupported = computed(() => {
  return VIDEO_MIME_TYPES.includes(mimeType.value as (typeof VIDEO_MIME_TYPES)[number])
})

const hasErrors = computed(() => {
  return isVideoFileToBig.value || isVideoDurationTooLong.value || !isVideoMimeTypeSupported.value
})

watch(dialog, () => {
  dialogWidth.value = undefined
})

const saveVideo = () => {
  if (videoFile.value && props.save) {
    props.save(videoFile.value)
  }
  $tracking?.event('Settings', 'Clicked', 'Video-save')
}

const onUploadClick = () => {
  $tracking?.event('Settings', 'Clicked', 'Upload Video')
  if (FileInput.value) {
    FileInput.value.value = ''
    FileInput.value.click()
  }
}

const onFileDrop = (event: DragEvent) => {
  event.preventDefault()
  if (FileInput.value) {
    FileInput.value.value = ''
  }
  if (event.dataTransfer?.files) {
    handleFileSelect({ target: { files: event.dataTransfer.files } } as any)
  }
}

const handleFileSelect = async (e: Event) => {
  const target = e.target as HTMLInputElement
  const files = Array.from(target.files || [])

  if (files.length === 0) return

  await onFileSelect(files[0])
}

const onFileSelect = async (file: File) => {
  videoFileSize.value = file.size
  mimeType.value = file.type
  videoFile.value = file

  if (typeof FileReader === 'function') {
    dialog.value = true
    processing.value = true

    videoUrl.value = URL.createObjectURL(file)

    const video = document.createElement('video')
    video.preload = 'metadata'

    video.onloadedmetadata = () => {
      videoDuration.value = video.duration
      processing.value = false
      window.URL.revokeObjectURL(video.src)

      const ratio = video.videoWidth / video.videoHeight
      dialogWidth.value = Math.max(600 * ratio, 600)
    }

    video.onerror = () => {
      processing.value = false
      alert(t('alerts.video-upload.error'))
      window.URL.revokeObjectURL(video.src)
    }

    video.src = URL.createObjectURL(file)
  } else {
    alert(t('alerts.video-upload.error'))
  }
}

onBeforeUnmount(() => {
  if (videoUrl.value) {
    URL.revokeObjectURL(videoUrl.value)
  }
})
</script>

<style scoped>
.video-preview-container {
  min-height: 300px;
}

.video-preview {
  max-height: 60vh;
  display: flex;
  justify-content: center;
  align-items: center;
}

.video-preview video {
  max-width: 100%;
  max-height: 60vh;
}

.drop-zone {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 300px;
  border: 2px dashed #aaa;
  border-radius: 10px;
  background-color: #f8f8f8;
  transition: border 0.3s ease-in-out;
  cursor: pointer;
}

.drop-zone__prompt {
  text-align: center;
  color: #aaa;
  font-size: 1.5rem;
}

.drop-zone:hover {
  border: 2px dashed #000;
}

@media (min-width: 960px) {
  .video-preview-container {
    flex-grow: 1 !important;
  }
}

@media (max-width: 960px) {
  .wrapper {
    padding-bottom: 70px !important;
  }
}
</style>
